import { useIntl } from '@/intl'
import { DriverForm } from '@/scopes/checkout/components/services/CarRentalFilteringService/Driver/DriverForm'
import {
  CarSelectedSheetCategoryText,
  CarSelectedSheetContent,
  CarSelectedSheetHintText,
  CarSelectedSheetTrailing,
  CarSelectedSheetTrigger,
  CarSelectedSheetTriggerContent,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/SheetViews'
import { useCarRentalServiceStore } from '@/scopes/checkout/components/services/CarRentalFilteringService/store'
import { createCarEvent } from '@/scopes/checkout/domain/analytics'
import { useService } from '@/scopes/checkout/domain/hooks'
import { type CheckoutCarRentalServiceOption, ServiceType } from '@/scopes/checkout/domain/types'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { Icon, ListItem, Sheet } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { useState } from 'react'

export function DriverSheet({ openByDefault = false, ...option }: CheckoutCarRentalServiceOption & { openByDefault?: boolean }) {
  const { service } = useService(ServiceType.CarRentalFiltering)
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { startEditingCar, endEditingCar,
  } = useCarRentalServiceStore()
  const [showDriverForm, setShowDriverForm] = useState(false)
  const { participants } = service || {}
  const { driverId, value: carValue, id: carId } = option

  const driver = driverId ? participants?.[driverId] : null
  React.useEffect(() => {
    if (openByDefault) {
      setTimeout(() => {
        setShowDriverForm(true)
      }, 300)
    }
  }, [setShowDriverForm, openByDefault])

  const handleClickDriverForm = () => {
    setShowDriverForm(true)
    startEditingCar(carId, {
      driverId: driverId ?? '',
      driverBirthDate: driver?.birthdate ? new Date(driver.birthdate) : new Date(),
      driverHasCreditCard: Boolean(driverId && driverId !== '0'),
      bookedEquipmentsWithQuantity: option.equipments.filter(({ id }) => option.selectedEquipments?.includes(id)).map(({ id, quantitySelected }) => {
        return {
          id,
          quantity: quantitySelected ?? 1,
        }
      }),
      isEditing: Boolean(driverId && driverId !== '0'),
    })
    sendEvent(
      createCarEvent({
        result: 'edit',
        type: carValue,
        description: 'edit button clicked',
        element: 'button',
      }),
    )
  }

  const handleEndEditing = () => {
    endEditingCar(carId)
    setShowDriverForm(false)
  }

  if (!service)
    return null
  if (!option)
    return null
  if (!participants)
    return null

  return (
    <Sheet.Root
      open={showDriverForm}
      onOpenChange={(open) => {
        if (!open) {
          handleEndEditing()
        }
      }}
    >
      <Sheet.Trigger>
        <CarSelectedSheetTrigger>
          <ListItem variant="tinted">
            <CarSelectedSheetTriggerContent onClick={handleClickDriverForm}>
              <Icon glyph="SteeringWheel" size={32} />
              <CarSelectedSheetContent>
                <CarSelectedSheetCategoryText $variant="body-s-regular-tight">{intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_driver_overline' })}</CarSelectedSheetCategoryText>
                <CarSelectedSheetCategoryText $variant="body-m-bold-tight">{driver ? driver.name : intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_driver_select_cell' })}</CarSelectedSheetCategoryText>
                <CarSelectedSheetHintText $variant="body-s-regular-tight">
                  {driver ? intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_driver_birthdate_hint' }, { birthdate: driver.birthdate }) : intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_driver_hint' })}
                </CarSelectedSheetHintText>
              </CarSelectedSheetContent>
              <CarSelectedSheetTrailing>
                <Icon glyph="ChevronRight" size={24} />
              </CarSelectedSheetTrailing>
            </CarSelectedSheetTriggerContent>
          </ListItem>
        </CarSelectedSheetTrigger>
      </Sheet.Trigger>
      <Sheet.Body side="right" hasPadding bottomSideInMobile>
        <Sheet.Header closeLabel={intl.formatMessage({ id: 'close' })} />
        <DriverForm {...option} showDriverForm setShowDriverForm={setShowDriverForm} />
      </Sheet.Body>
    </Sheet.Root>
  )
}
